import { useState, type CSSProperties, type ReactNode } from "react";
import { Avatar, Button, Dialog, OverflowMenu, vars } from "@pack-it-up/ui-web";
import type { Note } from "@pack-it-up/core";
import { findUrls } from "@pack-it-up/core";
import { TagField } from "./TagField.js";
import { RowTags } from "./RowTags.js";
import { formatRelativeTime } from "@pack-it-up/shared";
import { DragHandle } from "./DragHandle.js";
import type { SortableHandleProps } from "../../features/dnd/Sortable.js";

// URLs typed into the body are auto-detected (Doc 05 §6) — no separate "add a link"
// step. Renders the body text with recognized URLs swapped for inline anchors.
const linkifyBody = (text: string): ReactNode[] => {
  const matches = findUrls(text);
  if (matches.length === 0) return [text];
  const parts: ReactNode[] = [];
  let cursor = 0;
  matches.forEach((m, i) => {
    if (m.start > cursor) parts.push(text.slice(cursor, m.start));
    parts.push(
      <a key={i} href={m.url} style={{ color: vars.color.accent }}>
        {m.url}
      </a>,
    );
    cursor = m.end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
};

const editArea: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  minHeight: "64px",
  padding: vars.space.sm,
  border: `1px solid ${vars.color.border}`,
  borderRadius: vars.radius.md,
  background: vars.color.surface,
  color: vars.color.text,
  font: "inherit",
  resize: "vertical",
};

// One note in the feed. Shows author avatar, name, relative time, and body with
// auto-linkified URLs. Edit and delete live in a "More options" menu top-right;
// delete opens a confirm dialog.
export const NoteCard = ({
  note,
  authorName,
  isOwn,
  canEdit,
  tagPalette = [],
  dragHandleProps,
  onUpdate,
  onUpdateTags,
  onRemove,
}: {
  note: Note;
  authorName: string;
  isOwn: boolean;
  canEdit: boolean;
  /** Tags in use on this trip/template (T29). */
  tagPalette?: string[];
  // Present only in manual sort — renders the drag handle in front of the avatar.
  dragHandleProps?: SortableHandleProps;
  onUpdate: (body: string) => void;
  onUpdateTags: (tags: string[]) => void;
  onRemove: () => void;
}): ReactNode => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const cardRow: CSSProperties = {
    display: "flex",
    gap: vars.space.sm,
    marginBottom: vars.space.md,
    alignItems: "flex-start",
  };

  const bodyBox: CSSProperties = {
    flex: 1,
    minWidth: 0,
    background: vars.color.surface,
    border: `1px solid ${vars.color.border}`,
    borderRadius: vars.radius.md,
    padding: `10px 14px`,
    position: "relative",
  };

  if (editing) {
    return (
      <div style={cardRow}>
        {/* spacer to align with avatar in read mode */}
        <div style={{ width: 32, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <textarea
            aria-label="Edit note"
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            style={editArea}
          />
          <div style={{ marginTop: vars.space.sm }}>
            <TagField
              value={note.tags ?? []}
              palette={tagPalette}
              onChange={onUpdateTags}
            />
          </div>
          <div style={{ display: "flex", gap: vars.space.sm, marginTop: vars.space.sm }}>
            <Button
              onClick={() => {
                onUpdate(draft.trim());
                setEditing(false);
              }}
              disabled={draft.trim() === ""}
            >
              Save
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setDraft(note.body);
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={cardRow}>
        {dragHandleProps !== undefined && (
          <DragHandle listeners={dragHandleProps.listeners} attributes={dragHandleProps.attributes} />
        )}
        <Avatar name={authorName || "?"} size="sm" />
        <div style={bodyBox}>
          {/* note head: author name + relative time, "More options" top-right */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "8px",
              marginBottom: "4px",
            }}
          >
            <span style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
              <span
                style={{
                  fontSize: vars.font.sizeSm,
                  fontWeight: vars.font.weightSemibold,
                  color: vars.color.text,
                }}
              >
                {isOwn ? `${authorName} (you)` : authorName}
              </span>
              <span
                style={{
                  fontSize: vars.font.sizeXs,
                  color: vars.color.textMuted,
                }}
              >
                {formatRelativeTime(note.createdAt)}
              </span>
            </span>
            {canEdit && (
              <OverflowMenu
                label="More options for this note"
                items={[
                  { label: "Edit", onClick: () => setEditing(true) },
                  { label: "Delete note", onClick: () => setConfirmDelete(true), danger: true },
                ]}
              />
            )}
          </div>
          {/* body */}
          <p
            style={{
              margin: 0,
              fontSize: vars.font.sizeSm,
              lineHeight: vars.font.leadingBody,
              whiteSpace: "pre-wrap",
              color: vars.color.text,
            }}
          >
            {linkifyBody(note.body)}
          </p>
          {note.tags !== undefined && note.tags.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: vars.space.xs, marginTop: vars.space.sm }}>
              <RowTags tags={note.tags} />
            </div>
          )}
        </div>
      </div>
      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete this note?"
        compact
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                onRemove();
                setConfirmDelete(false);
              }}
            >
              Delete note
            </Button>
          </>
        }
      >
        <p style={{ margin: 0, color: vars.color.text, fontSize: vars.font.sizeSm }}>
          This removes the note for everyone on the trip. This can&apos;t be undone.
        </p>
      </Dialog>
    </>
  );
};
